import { EcommerceProductApi } from '@/types/Product';
import { Engine } from '@/engine/core/Engine';

export function useProduct() {
    const productStore = useProductStore();
    const sceneStore = useSceneStore();
    const stageStore = useStageStore();
    const siteStore = useSiteStore();

    function processProductData(data: Ref<EcommerceProductApi | null>) {
        if (!data.value?.product) {
            return;
        }
        const productData = data.value.product;
        productStore.setProduct(productData);

        // Initialiser Engine de manière sûre
        try {
            const engine = Engine.getInstance();
            if (engine && engine.historic) {
                engine.historic.getMetadata = productStore.getMetadata;
            }
        } catch (error) {
            console.warn('Engine not yet initialized in processProductData:', error);
        }

        stageStore.selectCategory(productData.categoryId);

        const lightList = productData.lightList.map((light) => light.object);
        sceneStore.setLights(lightList);
        sceneStore.setCameraSettings(productData.settingsCamera);
        // Positions caméra des 3 screenshots (BO). null / <3 => legacy (rotation 120°).
        sceneStore.setScreenshotSettings((productData as any).screenshotSettings);
    }

    function processEcommerceData(data: Ref<EcommerceProductApi | null>) {
        if (!data.value?.variants?.length || !data.value?.options?.length) {
            // Produit SANS mapping e-commerce (backend `/detailed` renvoie
            // `variants: null` → « mode catalogue »). On NE met PAS à jour le contexte
            // e-commerce : il reste sur le produit précédent. Le garde-fou
            // `ecommerceContextIsStale` (comparé au produit 3D chargé) empêche alors
            // l'add-to-cart d'envoyer le variantRef de ce produit périmé. Cf. bug
            // « fichiers bons mais MAUVAIS produit au panier » après changement de
            // produit sans mapping.
            return;
        }
        productStore.setEcommerceProduct(data.value);
        // Ce contexte e-commerce appartient à CE produit C3D (la donnée `/detailed`
        // porte variants + product). Sert au garde-fou anti-produit-périmé.
        productStore.setEcommerceOwner(data.value.product?.id ?? null);

        // Patch défensif : certaines intégrations (ex. Shopify) envoient un payload
        // `sendEcommerceProduct` sans `selectedVariant`, ou avec une variante absente
        // de `variants[]`. Sans garde, `selectedVariant.options` levait un TypeError
        // avalé par le try/catch de handleMessage : l'init s'interrompait avant
        // `askVariantsData`, laissant le bouton « Suivant » grisé sans erreur visible.
        // On retombe alors sur la première variante déclarée pour rester fonctionnel.
        const selectedVariant = data.value.selectedVariant?.options
            ? data.value.selectedVariant
            : data.value.variants[0];
        if (!selectedVariant?.options) {
            // variants[] non vide garanti par la garde ci-dessus, mais on reste défensif.
            return;
        }
        if (!data.value.selectedVariant?.options) {
            console.warn(
                '[useProduct] Payload sendEcommerceProduct sans `selectedVariant` valide : ' +
                    'fallback sur variants[0]. La variante présélectionnée peut être incorrecte — ' +
                    "l'intégration doit envoyer un `selectedVariant` correspondant à une entrée de `variants[]`.",
                { fallbackVariantRef: selectedVariant.variantRef }
            );
        }
        productStore.updateSelectedOptions(selectedVariant.options);
        // Quantité HORS PALIER reçue de l'hôte (boutique à saisie libre) : sans ça le
        // tunnel se bloque (aucune variante ne matche → prix null, bouton grisé). On la
        // traite comme une quantité libre → prix au palier ≤ quantité, aucun blocage.
        const displayedOption = productStore.ecommerceDisplayedOption;
        if (displayedOption) {
            const q = parseInt(String(selectedVariant.options[displayedOption.name] ?? '').match(/\d+/)?.[0] ?? '', 10);
            if (Number.isFinite(q) && !productStore.priceTierNumbers.includes(q)) {
                productStore.setFreeQuantity(q);
            }
        }
        if (siteStore.configuration.isIframeMode && productStore.ecommerceProduct.variants.length > 0) {
            const variantRefs: string[] = [];
            productStore.ecommerceProduct.variants.forEach((variant) => {
                variantRefs.push(variant.variantRef);
            });
            stageStore.expectedVariantType = 'product';
            const { askVariantsData } = usePostMessage();
            askVariantsData(variantRefs, data.value?.productHandle);
        }
    }

    return {
        processProductData,
        processEcommerceData
    };
}
